import React from 'react';
import { selectCurrentUser } from '../redux/selectors/user.selectors';
import { store } from '../redux/store';
import { active } from 'd3';

export const ENVIRONMENT = process.env.NODE_ENV;
export const isTesting = ENVIRONMENT === 'test';
export const isDev = ENVIRONMENT === 'development';

export const isDateValid = (dateString) => {
  var regex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateString.match(regex)) return false;

  var d = new Date(dateString);
  var dNum = d.getTime();
  if (!dNum && dNum !== 0) return false;

  return d.toISOString().slice(0, 10) === dateString;
};

export const getMonthName = (date) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return monthNames[new Date(date).getMonth()].toUpperCase();
};

export const getMonthsInDates = (startDate, endDate) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  let dateFrom = new Date(startDate);
  var dateTo = new Date(endDate);

  let fromYear = dateFrom.getFullYear();
  let toYear = dateTo.getFullYear();

  if (fromYear === toYear) {
    return monthNames.slice(dateFrom.getMonth(), dateTo.getMonth() + 1);
  } else {
    let months = addYear(
      monthNames.slice(dateFrom.getMonth(), new Date('1 December ' + fromYear)),
      fromYear,
    );
    for (let i = 1; i < toYear - fromYear; i++) {
      months = months.concat(addYear(monthNames, fromYear + i));
    }
    return months.concat(
      addYear(
        monthNames.slice(
          new Date('1 January ' + fromYear).getMonth(),
          dateTo.getMonth() + 1,
        ),
        toYear,
      ),
    );
  }
};

function addYear(months, year) {
  let updatedMonths = [];
  for (let i = 0; i < months.length; i++) {
    updatedMonths[i] = months[i] + ' ' + year;
  }

  return updatedMonths;
}

export const getBudgetCategories = (budget) => {
  // group budget items into categories
  const categories = [];

  budget.budgetItems.forEach((item) => {
    const categoryExists = categories.find(
      (category) => category.id === item.ledgerCategoryId,
    );

    if (categoryExists === undefined) {
      // item category does not exists, add it
      categories.push({
        id: item.ledgerCategoryId,
        description: item.ledgerCategoryDescription,
        uniqueId: item.ledgerCategoryUniqueId,
        groupDescription: item.ledgerCategoryGroupDescription,
        totalBudgetAmount: item.totalBudgetAmount,
        totalPeriodActual: 'N/A',
        variance: 'N/A',
        budgetItems: [item],
      });
    } else {
      // item exists, update and replace
      // find index of category
      const categoryIndex = categories.findIndex(
        (category) => category.id === categoryExists.id,
      );
      const updatedCategory = {
        ...categoryExists,
        totalBudgetAmount:
          categoryExists.totalBudgetAmount + item.totalBudgetAmount,
        budgetItems: [...categoryExists.budgetItems, item],
      };

      // replace category with updated one
      categories.splice(categoryIndex, 1, updatedCategory);
    }
  });

  return categories;
};

export const formatDate = (date) => {
  if (date) {
    if (typeof date === 'string') {
      return new Date(date).toDateString().replace(/^\S+\s/, '');
    }
    return date.toDateString().replace(/^\S+\s/, '');
  }

  return 'N/A';
};

export const formatCurrency = (amount) => {
  return `${amount?.toFixed(2)?.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}`;
};

export const sum = (lines, key = 'amount') => {
  let c = 0.0;
  lines.forEach((line) => {
    c = c + parseFloat(line[key]);
  });
  return formatCurrency(c);
};

var th_val = ['', 'thousand', 'million', 'billion', 'trillion'];
// System for uncomment this line for Number of English
// var th_val = ['','thousand','million', 'milliard','billion'];

var dg_val = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
];
var tn_val = [
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
var tw_val = [
  'Twenty',
  'Thirty',
  'Forty',
  'Fifty',
  'Sixty',
  'Seventy',
  'Eighty',
  'Ninety',
];
export function number2words(s) {
  return '';
}

export function getQueryString(obj) {
  if (!obj || typeof obj !== 'object') return '';

  return Object.entries(obj)
    .filter(([_, value]) => value != null && value !== '') // Exclude null, undefined, and empty string
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');
}

export const statusColor = {
  pending: 'text-orange',
  approved: 'text-success',
  completed: 'text-success',
  rejected: 'text-danger',
  reversed: 'text-danger',
  absent: 'text-danger',
  excused: 'text-danger',
  late: 'text-danger',
  present: 'text-success',
  generated: 'text-success',
  activated: 'text-success',
  deactivated: 'text-danger',
  active: 'text-success',
  inactive: 'text-danger',
};

export const getStatusColor = (status) => {
  if (!status || typeof status !== 'string') return '';
  return statusColor[status.toLowerCase()] || '';
};

export function useReducerSpread(initialArg) {
  const spreadReducer = (state, action) => ({
    ...state,
    ...action,
  });
  const [state, setState] = React.useReducer(
    spreadReducer,
    { ...initialArg },
    () => ({ ...initialArg }),
  );

  return [state, setState];
}

/**
 * Checks if the current date is within one week or less of the given date.
 * @param {string | Date} targetDate - The target date (string or Date object)
 * @returns {boolean} - Returns true if the difference is one week or less.
 */
export const isWithinOneWeek = (targetDate) => {
  const now = new Date();
  const date = new Date(targetDate);

  // Calculate the absolute difference in time in milliseconds
  const diffInMs = Math.abs(date - now);

  // Convert difference from milliseconds to days
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return diffInDays <= 7;
};

/**
 * Checks if the current time is within two minutes or less to the given date/time.
 * @param {string|Date} targetDateTime - The target date/time as a Date object or parseable string.
 * @returns {boolean} - Returns true if the target date/time is between now and 2 minutes from now.
 */
export const isWithinTwoMinutes = (targetDateTime) => {
  const now = new Date();
  const target = new Date(targetDateTime);

  // Calculate the difference in milliseconds (target time should be in the future)
  const diff = target - now;

  // Check that the target is in the future and within 2 minutes (2 * 60 * 1000 ms)
  return diff >= 0 && diff <= 2 * 60 * 1000;
};

export function redirectToBillingService(url, authToken) {
  let token = authToken || getCurrentUser()?.authToken || '';
  let baseUrl;
  const currentUrl = window.location.href;

  if (currentUrl.includes('staging')) {
    baseUrl =
      process.env.REACT_APP_BILLING_APP_URL ||
      'https://school-next-staging.epobssuite.com/';
  } else if (currentUrl.includes('localhost')) {
    baseUrl = 'http://localhost:4000/';
  } else {
    baseUrl =
      process.env.REACT_APP_BILLING_APP_URL ||
      'https://school-next.epobssuite.com/';
  }

  document.cookie = isDev
    ? `authToken=${token}; Path=/; SameSite=Lax; Max-Age=86400`
    : `authToken=${token}; Domain=epobssuite.com; Path=/; Secure; SameSite=Lax; Max-Age=86400`;

  window.open(`${baseUrl}${url}`, '_blank');
}

export function isSysAdmin(currentUser = getCurrentUser()) {
  if (!currentUser || !Array.isArray(currentUser.identityRoles)) return false;
  return currentUser.identityRoles.some((role) =>
    ['SystemAdministrator'].includes(role),
  );
}

export function isAdmin(currentUser = getCurrentUser()) {
  if (!currentUser || !Array.isArray(currentUser.identityRoles)) return false;
  return currentUser.identityRoles.some((role) =>
    ['SystemAdministrator', 'SchoolAdministrator'].includes(role),
  );
}

export function isAllowed(allowedRoles) {
  const currentUser = getCurrentUser();
  if (typeof allowedRoles === 'string') allowedRoles = [allowedRoles];
  if (!currentUser || !Array.isArray(allowedRoles)) return false;
  return currentUser.identityRoles.some((role) =>
    ['SystemAdministrator', 'SchoolAdministrator', ...allowedRoles].includes(
      role,
    ),
  );
}

export function hasSubscriptionAccess(currentUser, allowedRoles) {
  if (!currentUser) return false;
  return currentUser.identityRoles.some((role) =>
    [
      'SystemAdministrator',
      'SchoolAdministrator',
      'SubscriptionAccess',
      ...(allowedRoles ? allowedRoles : []),
    ].includes(role),
  );
}

export function formatCurrencyV2(
  value,
  currency = 'GHS',
  fractionalDigit = 2,
  locale = 'en-GH',
) {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: fractionalDigit,
  });

  const formattedValue = formatter.format(numericValue ?? 0);

  const decimalPart = formattedValue.includes('.')
    ? formattedValue.split('.')[1]
    : '';
  const isDecimalZeroes = decimalPart.includes('00');

  if (isDecimalZeroes) {
    return formattedValue.split('.')[0];
  }

  return formattedValue;
}

export const OPTIONS = {
  present: ['Present', 'Absent', 'Late', 'Excused'],
  enrolled: ['Enrolled', 'Graduated', 'Terminated'],
  activated: ['ACTIVATED', 'DEACTIVATED', 'PENDING'],
  revised: ['Revised', 'Reversed'],
  completed: ['Completed', 'Pending'],
  paymentModes: [
    { value: 'Cash', label: 'Cash' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Bank_Transfer', label: 'Bank Transfer' },
    { value: 'BankersDraft', label: 'Bankers Draft' },
    { value: 'MobileMoney', label: 'Mobile Money' },
    { value: 'StudentAccount', label: 'Student Deposit Account' },
    { value: 'DepositDeduction', label: 'Deposit Deduction' },
  ],
};

export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function makeDropdownOptions(items) {
  return items.map((item) => ({
    label: capitalize(item),
    value: item,
  }));
}

export function getItemById(items, id, key = 'id') {
  return items?.find((s) => String(s[key]) === String(id)) || {};
}

/**
 * Ensures an array has a minimum length by padding it with a specified value.
 * If the input is not an array, it's treated as an empty array.
 *
 * @param {Array<any>} array The input array, which can be `null` or `undefined`.
 * @param {number} length The desired minimum length of the array.
 * @param {any} [padValue=''] The value to use for padding. Defaults to an empty string.
 * @returns {Array<any>} A new array with the specified length. If the original array's
 *   length is greater than or equal to the desired length, it is returned as is.
 */
export const padArray = (array, length, padValue = '') => {
  const arr = Array.isArray(array) ? array : [];

  if (arr.length >= length) {
    return arr;
  }

  return [...arr, ...Array(length - arr.length).fill(padValue)];
};

export function getCurrentUser() {
  return selectCurrentUser(store.getState()) || {};
}

export function isToday(date) {
  if (!date) return false;
  const today = new Date();
  date = new Date(date.split('T')[0]);
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

export function getValueBasedOnEnv(envMap = {}, options = {}) {
  const currentUrl = window.location.href;

  for (const [envKey, value] of Object.entries(envMap)) {
    if (envKey === 'production') continue; // Skip 'production' keyword during matching
    if (currentUrl.includes(envKey)) {
      return value;
    }
  }

  // If no match found, return explicit production value or fallback
  if ('production' in envMap) {
    return envMap.production;
  }

  return options.fallback || '';

  // usage
  // const baseUrl = getValueBasedOnEnv({
  //   staging:
  //     process.env.REACT_APP_BILLING_APP_URL ||
  //     'https://school-next-staging.epobssuite.com/',
  //   localhost: 'http://localhost:4000/',
  //   production:
  //     process.env.REACT_APP_BILLING_APP_URL ||
  //     'https://school-next.epobssuite.com/',
  // });
}

/**
 * Checks if the given date/time has already passed (expired).
 * @param {string|Date} date - The date/time to check (ISO string or Date object)
 * @returns {boolean} - Returns true if the date/time is in the past.
 */
export function hasExpired(date) {
  if (!date) return false;
  const now = new Date();
  const target = new Date(date);
  return target < now;
}

export function isAllTrue(...args) {
  return args.every(Boolean);
}

export function isAnyTrue(...args) {
  return args.some(Boolean);
}

export const limitOptions = (total) => {
  return Array.from(new Set([10, 20, 30, 50, 100, total])).sort(
    (a, b) => a - b,
  );
};

export const today = new Date().toISOString().split('T')[0];

export function dateForComponent(date) {
  return new Date(date).toISOString().split('T')[0];
}

export const isEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

export const isPhoneNumber = (phone) => {
  const regex = /^(?:\+?233|0)?[25]\d{8}$/;
  return regex.test(phone);
};

/**
 * Strips the formatting a person is likely to type (spaces, hyphens, brackets)
 * so a number can be compared or sent in the form the API expects.
 */
export const normalizePhoneNumber = (phone) =>
  (phone ?? '').trim().replace(/[\s\-()]/g, '');

/**
 * Phone numbers accepted when registering a school. Deliberately stricter than
 * isPhoneNumber: it mirrors the API rule exactly (local 0XXXXXXXXX or
 * international +233XXXXXXXXX / 233XXXXXXXXX), so a number that passes here
 * cannot be rejected by the server afterwards. isPhoneNumber allows a bare
 * 9-digit number with no prefix, which the API refuses.
 */
export const isRegistrationPhoneNumber = (phone) => {
  const regex = /^(?:\+?233\d{9}|0\d{9})$/;
  return regex.test(normalizePhoneNumber(phone));
};

export function splitCapitalizedString(str) {
  return str.replace(/([A-Z])/g, ' $1').replace(/^ /, '');
}

export function splitUnderscoredString(str) {
  return str.replace(/_/g, ' ');
}

export const getPersonName = (person) => {
  if (!person) return 'N/A';
  if (person.fullName) return person.fullName;
  return `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'N/A';
};
